<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import DeepdiveLink from '../utils/DeepdiveLink.vue';
import Input from '@/components/utils/Input.vue';
import type { UserConfig } from '@/lib/interfaces/calculator';
import getDeepdiveUrl from '@/lib/getDeepdiveUrl';
import { useLocalStorage } from '@vueuse/core';
import type { CurrencyName } from '@consts'
import { availableCurrencies } from '@consts';

const userConfigInitialAmount = defineModel<UserConfig['initialAmount']>({ required: true })
const selectedCurrency = useLocalStorage<CurrencyName>('selected-currency', availableCurrencies.eur.name)

const inputIdCapital = 'calculator-input-capital'
const inputIdCurrency = 'calculator-input-capital-currency'


</script>

<template>
  <div class="flex justify-between items-center mt-4.5">
    <label 
      :for="inputIdCapital"
      class="font-semibold"
    >
      {{ $t('your_capital') }}
    </label>
    <DeepdiveLink label="Why" :href="getDeepdiveUrl('details/calculator/why-capital')" />
  </div>
  <!-- Capitals -->
  <div class="w-full h-max relative mt-2.5 flex items-center">
    <div class="absolute left-1.5 h-max z-10">
      <label
        :for="inputIdCurrency"
        class="sr-only"
      >
        {{ $t('currency') }}
      </label>
      <Listbox 
        :name="inputIdCurrency"
        v-if="selectedCurrency" 
        v-model="selectedCurrency"
      >
        <ListboxButton 
          :class="[
            'w-full rounded-2 border-brand-primary border-2 font-semibold text-brand-secondary',
            'flex justify-between',
            'p-2'
          ]"
        >
          <span :class="[availableCurrencies[selectedCurrency].icon, 'bg-brand-gradient']"></span>
          <span class="i-mdi:keyboard-arrow-down"></span>
        </ListboxButton>
        <ListboxOptions 
          :class="[
            'w-full min-w-max divide-y-2 divide-brand-stroke/10 mt-2 z-10 bg-brand-backgorund absolute bg-brand-background',
            'border-2 border-brand-primary rounded-3'
          ]"
        >
          <ListboxOption 
            v-for="(currency, index) in availableCurrencies" 
            :key="index" 
            :value="currency.name"
            :class="[
              'flex justify-between items-center cursor-pointer text-base sm:text-sm',
              'hover:bg-brand-stroke/10'
            ]" 
          >
            <div class="py-4 px-4 flex items-center gap-x-2.5">
              <span :class="[currency.icon, 'bg-white']"></span>
              {{ currency.label }}
            </div>
            <span v-if="availableCurrencies[selectedCurrency].label === currency.label" class="mr-4 opacity-40 i-ep:select" />
          </ListboxOption>
        </ListboxOptions>
      </Listbox>
    </div>
    <Input 
      :name="inputIdCapital"
      type="number"
      max="100000000000000"
      v-model="userConfigInitialAmount"
      class="w-full pl-13" 
    />
  </div>
</template>